import "./employment_section.scss";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body } from "@sprout/typography";
import { TextInput } from "@sprout/text_input";
import { type Option, SingleSelect } from "@sprout/select";
import { Checkbox } from "@sprout/checkbox";
import TrashIcon from "@sprout/icons/trash";
import { IconButton } from "@sprout/icon_button";
import type { EmploymentAnswer, EmploymentAnswers } from "../../utils/jben/build_application";
import { focusLastSection } from "../../utils/jben/focus_last_section";
import { useMonthOptions } from "../../hooks/use_month_options";

interface Props {
  employments: EmploymentAnswers;
  required: boolean;
  errors: Record<string, string>[];
  onChange: (employments: EmploymentAnswers) => void;
}

interface EmploymentProps {
  employment: EmploymentAnswer;
  required: boolean;
  showDelete: boolean;
  error: Record<string, string>;
  onChange: (employment: EmploymentAnswer) => void;
  onDelete: () => void;
}

const parseYear = (value: string): number | undefined => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const Employment = ({
  employment,
  required,
  showDelete,
  error,
  onChange,
  onDelete,
}: EmploymentProps) => {
  const { t } = useTranslation("job_post");
  const months = useMonthOptions();

  const selectedStartMonth = months.find((m) => m.value === employment.startDateMonth) || null;
  const selectedEndMonth = months.find((m) => m.value === employment.endDateMonth) || null;

  const idFor = (key: string) => `${key}-${employment.key}`;

  return (
    <div className="employment-form">
      <hr />
      <div className="header-row">
        <Body medium>{t("employment.employment")}</Body>
        {showDelete && (
          <IconButton label={t("employment.remove")} icon={TrashIcon} onClick={onDelete} />
        )}
      </div>
      <TextInput
        id={idFor("company-name")}
        required={required}
        label={t("employment.company_name")}
        maxLength={255}
        value={employment.companyName || ""}
        onChange={(event) => onChange({ ...employment, companyName: event.currentTarget.value })}
        error={error?.company_name}
      />
      <TextInput
        id={idFor("title")}
        required={required}
        label={t("employment.title")}
        maxLength={255}
        value={employment.title || ""}
        onChange={(event) => onChange({ ...employment, title: event.currentTarget.value })}
        error={error?.title}
      />
      <div className="date-row">
        <SingleSelect
          label={t("employment.start_date_month")}
          options={months}
          value={selectedStartMonth}
          onChange={(option) =>
            onChange({ ...employment, startDateMonth: (option?.value as number) || undefined })
          }
          required={required}
          clearable
          error={error?.start_date_month}
        />
        <TextInput
          id={idFor("start-date-year")}
          required={required}
          label={t("employment.start_date_year")}
          type="number"
          maxLength={4}
          value={employment.startDateYear?.toString() || ""}
          onChange={(event) =>
            onChange({ ...employment, startDateYear: parseYear(event.currentTarget.value) })
          }
          error={error?.start_date_year}
        />
      </div>
      <div className="date-row">
        <SingleSelect
          label={t("employment.end_date_month")}
          options={months}
          value={selectedEndMonth}
          onChange={(option) =>
            onChange({ ...employment, endDateMonth: (option?.value as number) || undefined })
          }
          required={required}
          clearable
          disabled={employment.currentRole}
          error={error?.end_date_month}
        />
        <TextInput
          id={idFor("end-date-year")}
          required={required}
          label={t("employment.end_date_year")}
          type="number"
          maxLength={4}
          value={employment.endDateYear?.toString() || ""}
          onChange={(event) =>
            onChange({ ...employment, endDateYear: parseYear(event.currentTarget.value) })
          }
          disabled={employment.currentRole}
          error={error?.end_date_year}
        />
        <Checkbox
          isMulti
          id={idFor("current-role")}
          options={[{ value: "1", label: t("employment.current_role") }]}
          values={employment.currentRole ? ["1"] : []}
          onSelect={(selected) =>
            onChange({ ...employment, currentRole: (selected as Option[]).length > 0 })
          }
        />
      </div>
    </div>
  );
};

export const EmploymentSection = ({ employments, required, errors, onChange }: Props) => {
  const { t } = useTranslation("job_post");
  const [shouldFocusSection, setShouldFocusSection] = useState<boolean>(false);

  useEffect(() => {
    if (shouldFocusSection) {
      focusLastSection(".employment-form");
      setShouldFocusSection(false);
    }
  }, [shouldFocusSection]);

  return (
    <div className="employment--container">
      {employments.map((employment, index) => (
        <Employment
          key={employment.key}
          employment={employment}
          required={required}
          showDelete={employments.length > 1}
          error={errors[index] || {}}
          onChange={(updated) => {
            const newEmployments = [...employments];
            newEmployments[index] = updated;
            onChange(newEmployments);
          }}
          onDelete={() => onChange(employments.filter((e) => e.key !== employment.key))}
        />
      ))}
      <button
        className="add-another-button"
        type="button"
        onClick={() => {
          const lastKey = (employments[employments.length - 1].key as number) ?? 0;
          onChange([...employments, { key: lastKey + 1 }]);
          setShouldFocusSection(true);
        }}
      >
        {t("employment.add_another")}
      </button>
    </div>
  );
};
